import { useEffect, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Coins, Trophy, Loader2, Minus, Plus, Crown, Clock, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { endpoints, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BuyPointsDialog } from "@/components/BuyPointsDialog";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString() : null);

export default function VoteDetail() {
  const { id } = useParams();
  const { isAuthed } = useAuth();
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);
  const [balance, setBalance] = useState(0);
  const [count, setCount] = useState(1);
  const [voting, setVoting] = useState(null);
  const [buyOpen, setBuyOpen] = useState(false);
  const [signup, setSignup] = useState(null); // {name, description, payoutPhone, imageUrl}
  const [applying, setApplying] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await endpoints.voteCampaign(id); setC(data); }
    catch (e) { toast.error(apiError(e?.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [id]);

  const loadBalance = useCallback(() => {
    if (!isAuthed) return;
    endpoints.pointsBalance().then((r) => setBalance(r.data.points)).catch(() => {});
  }, [isAuthed]);

  useEffect(() => {
    load(); loadBalance();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load, loadBalance]);

  const vote = async (optionId) => {
    if (!isAuthed) { window.dispatchEvent(new Event("open-auth")); return; }
    if (c.type === "paid" && balance < count * (c.pointsPerVote || 1)) {
      toast.error("Not enough points — buy more to vote."); setBuyOpen(true); return;
    }
    setVoting(optionId);
    try {
      await endpoints.castVote(id, optionId, c.type === "paid" ? count : 1);
      toast.success("Vote counted! 🎉");
      await load(); loadBalance();
    } catch (e) {
      const detail = apiError(e?.response?.data?.detail);
      toast.error(detail);
      if (String(detail).toLowerCase().includes("points")) setBuyOpen(true);
    } finally { setVoting(null); }
  };

  const onPhoto = async (file) => {
    if (!file) return;
    if (file.size > 1_500_000) { toast.error("Photo too large (max 1.5MB)"); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      try { const { data } = await endpoints.uploadContestantPhoto(reader.result); setSignup((s) => ({ ...s, imageUrl: data.url })); toast.success("Photo added"); }
      catch (e) { toast.error(apiError(e?.response?.data?.detail)); }
    };
    reader.readAsDataURL(file);
  };
  const submitApply = async () => {
    if (!signup?.name?.trim()) { toast.error("Enter your name"); return; }
    setApplying(true);
    try {
      const { data } = await endpoints.contestantApply(id, signup);
      toast.success(data.message || "Application submitted — pending approval");
      setSignup(null);
    } catch (e) { toast.error(apiError(e?.response?.data?.detail)); }
    finally { setApplying(false); }
  };

  if (loading) return <div className="flex justify-center py-32"><Loader2 className="w-8 h-8 animate-spin text-[#E11D2A]" /></div>;
  if (!c) return <div className="text-center py-32 text-slate-500">Campaign not found. <Link to="/vote" className="text-[#E11D2A]">Back to voting</Link></div>;

  const closed = c.status === "closed";
  const open = c.status === "open";
  const perVote = c.pointsPerVote || 1;

  return (
    <div className="px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto py-8">
      <Link to="/vote" className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-white mb-5"><ArrowLeft className="w-4 h-4" /> All campaigns</Link>

      <div className="rounded-2xl overflow-hidden border border-[#273042] bg-[#121620] mb-6">
        <div className="aspect-[21/9] relative bg-[#0A0C10]">
          <img src={c.coverUrl || "/bb-logo.png"} alt={c.title} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.src = "/bb-logo.png"; }} />
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 to-transparent" />
          <div className="absolute bottom-0 left-0 p-5">
            <div className="flex items-center gap-2 mb-2">
              {c.type === "paid" && <span className="text-[10px] font-extrabold uppercase px-2 py-1 rounded bg-[#F5B301]/20 text-[#F5B301] flex items-center gap-1"><Coins className="w-3 h-3" /> Paid · {perVote} pt/vote</span>}
              {c.isReward && <span className="text-[10px] font-extrabold uppercase px-2 py-1 rounded bg-[#2563EB]/20 text-[#93B4FF] flex items-center gap-1"><Trophy className="w-3 h-3" /> Reward</span>}
              <span className={`text-[10px] font-extrabold uppercase px-2 py-1 rounded ${open ? "bg-emerald-500/20 text-emerald-400" : closed ? "bg-slate-600/40 text-slate-300" : "bg-amber-500/20 text-amber-400"}`}>{c.status}</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-white">{c.title}</h1>
          </div>
        </div>
        <div className="p-5">
          {c.description && <p className="text-slate-300 text-sm">{c.description}</p>}
          <div className="flex flex-wrap items-center gap-4 mt-3 text-xs text-slate-400">
            <span>{(c.totalVotes ?? 0).toLocaleString()} total votes</span>
            {c.type === "paid" && <span>{(c.totalAmountFrw ?? 0).toLocaleString()} RWF collected</span>}
            {c.startAt && <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> Opens {fmtDate(c.startAt)}</span>}
            {c.closeAt && <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> Closes {fmtDate(c.closeAt)}</span>}
          </div>
        </div>
      </div>

      {c.type === "paid" && isAuthed && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[#F5B301]/30 bg-[#F5B301]/5 p-4 mb-6" data-testid="points-wallet">
          <div className="flex items-center gap-2 text-white font-bold"><Coins className="w-5 h-5 text-[#F5B301]" /> {balance.toLocaleString()} points</div>
          <div className="flex items-center gap-3">
            {open && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-400">Votes</span>
                <div className="flex items-center rounded-lg border border-[#273042] bg-[#0A0C10]">
                  <button onClick={() => setCount((n) => Math.max(1, n - 1))} data-testid="vote-count-dec" className="w-9 h-9 text-slate-300 hover:text-white flex items-center justify-center"><Minus className="w-4 h-4" /></button>
                  <span className="w-8 text-center text-white font-bold" data-testid="vote-count">{count}</span>
                  <button onClick={() => setCount((n) => n + 1)} data-testid="vote-count-inc" className="w-9 h-9 text-slate-300 hover:text-white flex items-center justify-center"><Plus className="w-4 h-4" /></button>
                </div>
                <span className="text-xs text-[#F5B301] font-semibold" data-testid="vote-cost-preview">= {(count * perVote).toLocaleString()} pts · {(count * perVote * (c.pointValueFrw || 10)).toLocaleString()} RWF</span>
              </div>
            )}
            <button onClick={() => setBuyOpen(true)} data-testid="buy-points-btn" className="h-10 px-4 rounded-lg bg-[#F5B301] hover:bg-[#e0a400] text-black font-bold text-sm flex items-center gap-1.5"><Coins className="w-4 h-4" /> Buy points</button>
          </div>
        </div>
      )}

      {closed && c.winner && (
        <div className="rounded-xl border border-[#E11D2A]/40 bg-[#E11D2A]/10 p-4 mb-6 flex items-center gap-3" data-testid="vote-winner">
          <Crown className="w-8 h-8 text-[#F5B301]" />
          <div><div className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Winner</div>
            <div className="text-xl font-extrabold text-white">{c.winner.name}</div>
            <div className="text-xs text-slate-300">{c.winner.votes.toLocaleString()} votes · {c.winner.percent}%</div></div>
        </div>
      )}

      <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-3">Results · ranked 1st to last</div>
      <div className="space-y-3" data-testid="vote-options">
        {(c.options || []).length === 0 ? <div className="text-slate-500 text-sm">No participants yet.</div> :
          c.options.map((o) => (
            <div key={o.id} data-testid={`vote-option-${o.id}`} className="rounded-xl border border-[#273042] bg-[#121620] p-4">
              <div className="flex items-center gap-3">
                <div className={`w-9 h-9 rounded-full flex items-center justify-center font-extrabold text-sm shrink-0 ${o.rank === 1 ? "bg-[#F5B301] text-black" : "bg-[#273042] text-slate-200"}`}>{o.rank}</div>
                {o.imageUrl && <img src={o.imageUrl} alt={o.name} className="w-11 h-11 rounded-lg object-cover shrink-0" />}
                <div className="min-w-0 flex-1">
                  <div className="text-white font-bold truncate">{o.name}</div>
                  {o.description && <div className="text-xs text-slate-400 truncate">{o.description}</div>}
                </div>
                <div className="text-right shrink-0">
                  <div className="text-white font-extrabold" data-testid={`option-votes-${o.id}`}>{(o.votes ?? 0).toLocaleString()}</div>
                  <div className="text-xs text-slate-400">{o.percent}%</div>
                </div>
              </div>
              <div className="mt-3 h-2 rounded-full bg-[#0A0C10] overflow-hidden">
                <div className="h-full rounded-full bg-gradient-to-r from-[#E11D2A] to-[#F5B301] transition-all duration-700" style={{ width: `${o.percent || 0}%` }} />
              </div>
              {open && (
                <button onClick={() => vote(o.id)} disabled={voting === o.id || (c.type === "free" && c.hasVoted)}
                  data-testid={`vote-btn-${o.id}`}
                  className="mt-3 w-full h-10 rounded-lg bg-[#E11D2A] hover:bg-[#C8121F] text-white font-bold text-sm btn-glow flex items-center justify-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed">
                  {voting === o.id ? <Loader2 className="w-4 h-4 animate-spin" />
                    : c.type === "free" ? (c.hasVoted ? "You voted" : "Vote")
                    : `Vote (${count * perVote} pts · ${(count * perVote * (c.pointValueFrw || 10)).toLocaleString()} RWF)`}
                </button>
              )}
            </div>
          ))}
      </div>

      {c.type === "free" && c.hasVoted && <p className="text-center text-xs text-emerald-400 mt-4">✓ Thanks for voting! You can vote once in this campaign.</p>}

      <BuyPointsDialog open={buyOpen} onClose={() => setBuyOpen(false)} pointValueFrw={c.pointValueFrw || 10}
        onCredited={() => { loadBalance(); toast.success("Points added — cast your vote!"); }} />

      {c.allowSignups && c.status !== "closed" && (
        <div className="mt-8 rounded-2xl border border-[#2563EB]/30 bg-[#2563EB]/5 p-5" data-testid="contestant-signup">
          <div className="flex items-center gap-2 text-white font-bold"><UserPlus className="w-5 h-5 text-[#93B4FF]" /> Register as a contestant</div>
          <p className="text-slate-400 text-sm mt-1">Submit your entry. It appears for voting once an admin approves it.</p>
          {!signup ? (
            <button onClick={() => setSignup({ name: "", description: "", payoutPhone: "", imageUrl: "" })} data-testid="open-signup-btn"
              className="mt-3 h-10 px-4 rounded-lg bg-[#2563EB] hover:bg-[#1d4ed8] text-white font-bold text-sm">Register now</button>
          ) : (
            <div className="mt-4 space-y-3">
              <input value={signup.name} onChange={(e) => setSignup({ ...signup, name: e.target.value })} placeholder="Your name / stage name" data-testid="signup-name"
                className="w-full h-11 rounded-xl bg-[#0A0C10] border border-[#273042] px-4 text-white outline-none focus:border-[#2563EB]/60" />
              <textarea value={signup.description} onChange={(e) => setSignup({ ...signup, description: e.target.value })} placeholder="Short profile / description" rows={2}
                className="w-full rounded-xl bg-[#0A0C10] border border-[#273042] px-4 py-2.5 text-white outline-none focus:border-[#2563EB]/60" />
              {c.isReward && <input value={signup.payoutPhone} onChange={(e) => setSignup({ ...signup, payoutPhone: e.target.value })} placeholder="Mobile money payout number (if you win)" data-testid="signup-payout"
                className="w-full h-11 rounded-xl bg-[#0A0C10] border border-[#273042] px-4 text-white outline-none focus:border-[#2563EB]/60" />}
              <div className="flex items-center gap-3">
                <label className="h-10 px-4 rounded-lg border border-[#273042] bg-[#0A0C10] text-slate-300 text-sm cursor-pointer flex items-center gap-2">
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => onPhoto(e.target.files?.[0])} data-testid="signup-photo" /> Upload photo
                </label>
                {signup.imageUrl && <img src={signup.imageUrl} alt="preview" className="w-10 h-10 rounded-lg object-cover" />}
              </div>
              <div className="flex gap-2">
                <button onClick={submitApply} disabled={applying} data-testid="submit-signup-btn" className="h-10 px-4 rounded-lg bg-[#2563EB] hover:bg-[#1d4ed8] text-white font-bold text-sm flex items-center gap-2 disabled:opacity-60">{applying ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Submit entry</button>
                <button onClick={() => setSignup(null)} className="h-10 px-4 rounded-lg text-slate-400 hover:text-white text-sm">Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
